import React, { useMemo } from 'react';
import SortableTable from '../../SortableTable.jsx';
import { SectionCard } from './shared.jsx';
import { formatDateTime, getOptionsSummary, getToneClasses } from './utils.js';

const BIConfigEntriesTable = ({ configs, modulesMetadata, onEdit, onDelete }) => {
  const columns = useMemo(() => ([
    {
      key: 'module',
      label: 'Module',
      render: (config) => {
        const moduleMetadata = modulesMetadata.find((module) => module.module === config.module);

        return (
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900 dark:text-white">{moduleMetadata?.label || config.module}</span>
            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{config.module}</span>
          </div>
        );
      },
      rawValue: (config) => config.module
    },
    {
      key: 'discriminant_id',
      label: 'Target',
      render: (config) => (
        <div className="flex flex-col">
          <span className="text-sm text-gray-900 dark:text-white">Project</span>
          <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{config.discriminant_id}</span>
        </div>
      ),
      rawValue: (config) => config.discriminant_id
    },
    {
      key: 'options',
      label: 'Rules',
      filterable: false,
      render: (config) => {
        const summary = getOptionsSummary(config);

        return (
          <div className="flex flex-col gap-2 min-w-[220px]">
            <span className={`inline-flex w-fit items-center rounded-full border px-2.5 py-1 text-xs font-medium ${getToneClasses(summary.tone)}`}>
              {summary.title}
            </span>
            <div className="flex flex-wrap gap-2">
              {summary.details.map((detail) => (
                <span
                  key={detail}
                  className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                >
                  {detail}
                </span>
              ))}
            </div>
          </div>
        );
      },
      rawValue: (config) => JSON.stringify(config.options || {})
    },
    {
      key: 'updated_by_username',
      label: 'Updated by',
      render: (config) => (
        <span className="text-gray-700 dark:text-gray-300">
          {config.updated_by_username || '-'}
        </span>
      )
    },
    {
      key: 'updated_at',
      label: 'Updated at',
      render: (config) => (
        <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
          {formatDateTime(config.updated_at)}
        </span>
      ),
      rawValue: (config) => config.updated_at || ''
    },
    {
      key: 'actions',
      label: 'Actions',
      filterable: false,
      render: (config) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(config)}
            className="inline-flex items-center justify-center rounded-lg p-2 text-blue-600 hover:bg-blue-50 hover:text-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30 dark:hover:text-blue-200"
            title="Edit configuration"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(config)}
            className="inline-flex items-center justify-center rounded-lg p-2 text-red-600 hover:bg-red-50 hover:text-red-800 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-200"
            title="Delete configuration"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8" />
            </svg>
          </button>
        </div>
      )
    }
  ]), [modulesMetadata, onDelete, onEdit]);

  return (
    <SectionCard
      title="Configuration entries"
      subtitle="Review, edit or remove project-specific rules by module."
      bodyClassName="min-h-0"
    >
      <div className="h-[520px] min-h-[360px]">
        <SortableTable
          data={configs}
          columns={columns}
          emptyMessage="No BI configuration found"
          countLabel={`Total: ${configs.length} configuration${configs.length > 1 ? 's' : ''}`}
          initialSortBy={{ key: 'module', direction: 'ascending' }}
          fullHeight={true}
          globalSearch={true}
        />
      </div>
    </SectionCard>
  );
};

export default BIConfigEntriesTable;
